import * as Phaser from "phaser-ce"
import { Align, FontType } from "../../../common/TextUtils"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { TransitionState } from "../../GameState"
import { Buttons, ButtonSize, ButtonType } from "../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { loadAssets, TtAudio, TtImage } from "./TobaccoTourAssets"
import { TobaccoTourProgressStore } from "./TobaccoTourProgressStore"

export class TobaccoTourState extends Phaser.State {
    tobaccoGroup: Phaser.Group

    async preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        if (!PhaserUtils.loggedIn()) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }

        ColorUtils.setBackgroundColorGray(this.game)
        Scale.resizeScreen(this.game, true)

        const background = Sprites.add(this.game, TtImage.TABLE)
        Sprites.alignWithScreenBottom(background)
        Sprites.centerInWorldXOnly(background)

        Sprites.add(this.game, TtImage.MICROSCOPE, {
            x: Scale.x(0.578),
            y: Scale.y(0.103),
        })

        this.tobaccoGroup = this.game.add.group()

        const cigFinished = TobaccoTourProgressStore.instance.cigStageFinished
        const cigSprite = new ExpandoSprite(
            this.game,
            Scale.x(0.243),
            Scale.y(0.382),
            TtImage.CIGARETTES,
            {
                on: !cigFinished,
                group: this.tobaccoGroup,
            },
        )
        cigSprite.addOnClick(() =>
            this.game.state.start(TransitionState.TOBACCO_TOUR_CIG),
        )
        if (cigFinished) {
            cigSprite.grayOut()
        }

        const chewFinished = TobaccoTourProgressStore.instance.chewStageFinished
        const chewSprite = new ExpandoSprite(
            this.game,
            Scale.x(0.01),
            Scale.y(0.643),
            TtImage.CHEW,
            {
                on: !chewFinished,
                group: this.tobaccoGroup,
            },
        )
        chewSprite.addOnClick(() =>
            this.game.state.start(TransitionState.TOBACCO_TOUR_CHEW),
        )
        if (chewFinished) {
            chewSprite.grayOut()
        }

        const ecigFinished = TobaccoTourProgressStore.instance.ecigStageFinished
        const ecigSprite = new ExpandoSprite(
            this.game,
            Scale.x(0.473),
            Scale.y(0.587),
            TtImage.ECIG_LIQUID,
            {
                scale: 0.25,
                on: !ecigFinished,
                group: this.tobaccoGroup,
            },
        )
        ecigSprite.addOnClick(() =>
            this.game.state.start(TransitionState.ECIG_TOUR),
        )
        if (ecigFinished) {
            ecigSprite.grayOut()
        }

        PhaserText.create(this.game, "Tobacco\nTour", {
            x: Scale.x(0.126),
            y: Scale.y(0.076),
            fontPx: 48,
            type: FontType.FUTURISTIC,
            align: Align.CENTER,
        })

        switch (true) {
            case TobaccoTourProgressStore.instance.tobaccoCompletedStages
                .length === 0: {
                Audio.add(this.game, TtAudio.INTRO, {
                    onPlay: () => this.disableTobacco(),
                    onStop: () => this.enableTobacco(),
                })
                break
            }
            case TobaccoTourProgressStore.instance.reviewComplete: {
                Audio.add(this.game, TtAudio.FINISHED_REVIEW, {
                    onStop: () => {
                        Buttons.create(
                            this.game,
                            "Next",
                            () => {
                                TobaccoTourProgressStore.destroy()
                                Transitions.startOutro(
                                    this.game,
                                    ActivityKey.TOBACCO_TOUR,
                                )
                            },
                            { x: Scale.x(0.89), y: Scale.y(0.89) },
                        )
                    },
                })
                break
            }
            case TobaccoTourProgressStore.stagesComplete: {
                const button = Buttons.create(
                    this.game,
                    "Let's Review",
                    () =>
                        this.game.state.start(
                            TransitionState.TOBACCO_TOUR_REVIEW,
                        ),
                    {
                        y: Scale.y(0.35),
                        buttonType: ButtonType.PRIMARY,
                        buttonSize: ButtonSize.WIDE,
                        centerInWorldX: true,
                    },
                )
                Audio.add(this.game, TtAudio.REVIEW, {
                    onPlay: () => button.disable(),
                    onStop: () => button.enable(),
                })
                break
            }
            default: {
                Audio.add(this.game, TtAudio.ANOTHER_FORM, {
                    onPlay: () => this.disableTobacco(),
                    onStop: () => this.enableTobacco(),
                })
            }
        }
    }

    private disableTobacco = () =>
        this.tobaccoGroup.forEach((child: ExpandoSprite) => (child.on = false))

    private enableTobacco = () =>
        this.tobaccoGroup.forEach((child: ExpandoSprite) => (child.on = true))
}
